// What must be true before installation can start: each specialist's Ready to build
// checklist, every unmet condition opening to the open actions resolving it. The
// specialist's tab shows its own; the Overview shows every agent's through the same
// items, so the two read alike. An opened item shows its actions as the Next actions
// table's own rows. See docs/Y2026-Q4/design-ready-to-build-checklists.md.
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import {
  AREA_LABEL,
  AREA_ORDER,
  areaLabel,
} from "../api/hooks/buildingReadiness";
import type {
  NextAction,
  ProjectAgentStatus,
  ProjectAgentStatusList,
  ReadyCheck,
} from "../api/hooks/useProjectAgentStatus";
import { projectSpecialistPath, registerPath } from "../routes/learningAgentPaths";
import { formatDay } from "../utils/attributeFormat";
import { reviewAttrs } from "../utils/reviewState";
import { workingSpecialists } from "../utils/standDown";
import {
  CHECK_STATE_LABEL,
  checkState,
  itemFacts,
  NO_ACTION_NO_REASON,
  type ProjectItems,
  projectItems,
  projectSource,
  resolvingActions,
  shownDue,
  worksTowards,
} from "../utils/nextActions";
import { DependencyLink, FromAgent } from "./ActionDetails";
import { ActionTable } from "./NextActionTable";
import { Fold } from "./Fold";
import { SectionBlock, SectionCard } from "./SpecialistRowSections";
import { CITED_BASIS, useReadyCheckEvidence } from "./useReadyCheckEvidence";
import { useReadyToBuildOpen } from "./useReadyToBuildOpen";
import styles from "./ReadyToBuild.module.css";

type Cite = (text: string) => ReactNode;

const openChecks = (entry: ProjectAgentStatus | undefined): ReadyCheck[] =>
  (entry?.lists?.ready_checks ?? []).filter((c) => !c.closed);

// The date an action works to: the deadline it protects when it has one, else its own.
const workingDate = (action: NextAction, items: ProjectItems): string =>
  (action.serves && items.dates.get(action.serves)?.date.date) ||
  shownDue(action).date;

// What still stands between the project and the build, most pressing first: unmet items
// nobody is working on (a wait that has ended included), then those being worked, soonest
// date first (undated after), then those waiting on others, then the met ones. The sort
// is stable, so ties keep the agent's order.
const GROUP_ORDER: Record<ReturnType<typeof checkState>, number> = {
  nobody: 0,
  wait_ended: 0,
  worked: 1,
  waiting: 2,
  met: 3,
};

const ordered = (checks: ReadyCheck[], items: ProjectItems): ReadyCheck[] => {
  const group = (c: ReadyCheck): number => GROUP_ORDER[checkState(c, items)];
  const soonest = (c: ReadyCheck): string =>
    resolvingActions(items, c.check_id)
      .map((a) => workingDate(a, items))
      .filter(Boolean)
      .sort()[0] ?? "~";
  return [...checks].sort(
    (a, b) => group(a) - group(b) || soonest(a).localeCompare(soonest(b)),
  );
};

const metCount = (checks: { met: boolean }[]): string =>
  `${checks.filter((c) => c.met).length} of ${checks.length} met`;

// Building readiness's checks under its areas, in the order its area cards show them, any
// without an area last. A checklist with no areas stays one list.
const byArea = (
  checks: ReadyCheck[],
): { label: string; checks: ReadyCheck[] }[] => {
  if (!checks.some((c) => c.area)) return [{ label: "", checks }];
  const known = AREA_ORDER.map((area) => ({
    label: AREA_LABEL[area],
    checks: checks.filter((c) => c.area === area),
  }));
  const other = checks.filter((c) => !areaLabel(c.area));
  return [...known, { label: "Other", checks: other }].filter(
    (g) => g.checks.length > 0,
  );
};

const BasisLine = ({ check, cite }: { check: ReadyCheck; cite: Cite }) => {
  const cited = CITED_BASIS[check.basis];
  if (cited)
    return (
      <span className={styles.proof}>
        From {cited} {cite(check.basis_source)}
      </span>
    );
  if (check.basis === "playbook")
    return (
      <span className={styles.proof}>
        {/* An item of what we need from Amazon names its clause, not a rule. */}
        {check.need
          ? check.basis_source
          : `From the agent's rule: ${check.basis_source}`}
      </span>
    );
  return null;
};

/** Where a check comes from: its basis, and each register line that states it as a link to
 * that clause, so one resting on a standard and the client's spec shows both. */
export const CheckSource = ({
  check,
  cite,
  projectId,
}: {
  check: ReadyCheck;
  cite: Cite;
  projectId: string;
}) => (
  <>
    <BasisLine check={check} cite={cite} />
    {(check.register_lines ?? []).length > 0 && (
      <span className={styles.proof}>
        {"Stated in "}
        {(check.register_lines ?? []).map((line, i) => (
          <span key={line.line_id}>
            {i > 0 && ", "}
            <Link
              to={registerPath(line.document_id, projectId)}
              title={line.title}
            >
              {line.document_name} {line.reference}
            </Link>
          </span>
        ))}
      </span>
    )}
  </>
);

// A condition added so an action had one to work towards, which no document or rule
// states yet: shown so an operator can challenge it.
export const ForActionChip = ({ check }: { check: ReadyCheck }) =>
  check.basis === "action" ? (
    <span
      className={styles.forAction}
      title="Added so an action had a condition to work towards; no document or rule states it yet."
    >
      Added for an action
    </span>
  ) : null;

/** What a waiting check waits for: each item it names as a link, then the reason; with
 * none named, the reason alone. */
export const WaitingFor = ({
  check,
  items,
  team,
  projectId,
}: {
  check: ReadyCheck;
  items: ProjectItems;
  team: Record<string, string>;
  projectId: string;
}) => {
  const href = (agent: string, id: string) =>
    `${projectSpecialistPath(projectId, agent)}#${id}`;
  if (check.waits_for.length === 0)
    return (
      <p className={styles.reason}>
        <strong>Waiting for:</strong> {check.no_action_reason}
      </p>
    );
  return (
    <div className={styles.reason}>
      <strong>Waiting for:</strong>
      <ul className={styles.waits}>
        {check.waits_for.map((id) => {
          const facts = itemFacts(id, items, team);
          return (
            <li key={id}>
              <DependencyLink
                item={id}
                items={items}
                team={team}
                href={href}
              />
              {facts && <FromAgent agent={facts.agent} />}
            </li>
          );
        })}
      </ul>
      {check.no_action_reason}
    </div>
  );
};

const CheckItem = ({
  check,
  resolving,
  agent,
  projectId,
  data,
  items,
  cite,
}: {
  check: ReadyCheck;
  resolving: NextAction[];
  agent: string;
  projectId: string;
  data: ProjectAgentStatusList;
  items: ProjectItems;
  cite: Cite;
}) => {
  const state = checkState(check, items);
  if (check.met)
    return (
      <li className={styles.item} id={check.check_id}>
        <span className={styles.markMet} aria-hidden="true">
          ✓
        </span>
        <span>
          {check.text}
          <span className={styles.srOnly}> (met)</span>
          <ForActionChip check={check} />
          <span className={styles.proof}>
            Met {formatDay(check.met_on)} {cite(check.evidence)}
          </span>
          <CheckSource check={check} cite={cite} projectId={projectId} />
        </span>
      </li>
    );
  return (
    <li className={styles.item} id={check.check_id}>
      <span className={styles.markUnmet} aria-hidden="true">
        ✕
      </span>
      <details className={styles.unmet}>
        <summary>
          {check.text}
          <span className={styles.srOnly}> (not met)</span>
          <ForActionChip check={check} />
          {state === "worked" ? (
            <span className={styles.count}>
              {resolving.length} action{resolving.length === 1 ? "" : "s"}
            </span>
          ) : (
            <span
              className={
                state === "waiting"
                  ? styles.count
                  : `${styles.count} ${styles.nobody}`
              }
              title={
                state === "wait_ended"
                  ? "Everything it waited for has closed"
                  : state === "waiting"
                    ? check.no_action_reason
                    : undefined
              }
            >
              {CHECK_STATE_LABEL[state]}
            </span>
          )}
        </summary>
        <CheckSource check={check} cite={cite} projectId={projectId} />
        {resolving.length > 0 ? (
          <div className={styles.actions}>
            {/* The page's own action rows; its Next actions table holds the anchors. */}
            <ActionTable
              scope={{ kind: "agent", agent }}
              sources={[projectSource(projectId, data)]}
              include={(action) => worksTowards(action, check.check_id)}
              empty=""
              controls={false}
              showArea={false}
              anchored={false}
            />
          </div>
        ) : check.no_action_reason ? (
          <WaitingFor
            check={check}
            items={items}
            team={data.team}
            projectId={projectId}
          />
        ) : (
          <p className={styles.reason}>{NO_ACTION_NO_REASON}</p>
        )}
      </details>
    </li>
  );
};

const CheckList = ({
  entry,
  data,
  projectId,
  cite,
}: {
  entry: ProjectAgentStatus;
  data: ProjectAgentStatusList;
  projectId: string;
  cite: Cite;
}) => {
  const items = projectItems(data);
  const checks = ordered(openChecks(entry), items);
  if (checks.length === 0)
    return (
      <p className={styles.muted}>
        No checklist written yet; the next pass that works this project writes
        one.
      </p>
    );
  const list = (group: ReadyCheck[]) => (
    <ul className={styles.checks}>
      {group.map((check) => (
        <CheckItem
          key={check.check_id}
          check={check}
          resolving={resolvingActions(items, check.check_id)}
          agent={entry.agent}
          projectId={projectId}
          data={data}
          items={items}
          cite={cite}
        />
      ))}
    </ul>
  );
  const groups = byArea(checks);
  if (groups.length === 1 && !groups[0].label) return list(checks);
  return (
    <>
      {groups.map((group) => (
        <div key={group.label} className={styles.area}>
          <h5 className={styles.areaHead}>
            {group.label}
            <span className={styles.small}>{metCount(group.checks)}</span>
          </h5>
          {list(group.checks)}
        </div>
      ))}
    </>
  );
};

/** One specialist's checklist, as its own tab shows it: the card's body, without the
 * card, for a page that frames it itself (Building readiness). */
export const ReadyToBuildBody = ({
  agent,
  projectId,
  data,
}: {
  agent: string;
  projectId: string;
  data: ProjectAgentStatusList;
}) => {
  const entry = data.agents.find((a) => a.agent === agent);
  const { cite, modal } = useReadyCheckEvidence(openChecks(entry));
  if (!entry) return null;
  return (
    <>
      {modal}
      <CheckList entry={entry} data={data} projectId={projectId} cite={cite} />
    </>
  );
};

/** "N of M met" for one specialist, for a header beside its title. */
export const ReadyToBuildCount = ({
  agent,
  data,
}: {
  agent: string;
  data: ProjectAgentStatusList;
}) => {
  const checks = openChecks(data.agents.find((a) => a.agent === agent));
  return checks.length > 0 ? (
    <span className={styles.small}>{metCount(checks)}</span>
  ) : null;
};

export const ReadyToBuildCard = ({
  agent,
  projectId,
  data,
}: {
  agent: string;
  projectId: string;
  data: ProjectAgentStatusList;
}) => (
  <SectionCard
    title="Ready to build"
    aside={<ReadyToBuildCount agent={agent} data={data} />}
    id="ready-to-build"
    folded={{ open: useReadyToBuildOpen() }}
  >
    <SectionBlock>
      <ReadyToBuildBody agent={agent} projectId={projectId} data={data} />
    </SectionBlock>
  </SectionCard>
);

/** Every specialist's checklist on the project, each under its name; one stood down from
 * the project has none to show. */
export const ReadyToBuildOverview = ({
  projectId,
  data,
}: {
  projectId: string;
  data: ProjectAgentStatusList;
}) => {
  const keepers = workingSpecialists(data.agents);
  const all = keepers.flatMap((a) => openChecks(a));
  const { cite, modal } = useReadyCheckEvidence(keepers.flatMap((a) => openChecks(a)));
  const open = useReadyToBuildOpen();
  if (keepers.length === 0) return null;
  return (
    <section className={styles.overview} id="ready-to-build">
      {modal}
      <Fold
        headClassName={styles.overviewHead}
        open={open}
        head={
          <>
            <h3>Ready to build</h3>
            <span className={styles.spacer} />
            {all.length > 0 && (
              <span className={styles.small}>{metCount(all)}</span>
            )}
          </>
        }
      >
        <div className={styles.groups}>
          {keepers.map((entry) => {
            const checks = openChecks(entry);
            return (
              <div
                key={entry.agent}
                className={styles.group}
                {...reviewAttrs(
                  "specialist_summary",
                  `${entry.label} ready to build`,
                  {
                    agent: entry.agent,
                  },
                )}
              >
                <Fold
                  headClassName={styles.groupHead}
                  open={open}
                  head={
                    <h4>
                      <Link
                        to={`${projectSpecialistPath(projectId, entry.agent)}#ready-to-build`}
                      >
                        {entry.label}
                      </Link>
                      {checks.length > 0 && (
                        <span className={styles.small}>{metCount(checks)}</span>
                      )}
                    </h4>
                  }
                >
                  <CheckList
                    entry={entry}
                    data={data}
                    projectId={projectId}
                    cite={cite}
                  />
                </Fold>
              </div>
            );
          })}
        </div>
      </Fold>
    </section>
  );
};
